import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useApi } from '../services/api';
import { UserRole } from '@bst-stellar/shared';
import TipCard, { TipCardSkeleton } from '../components/TipCard';
import RewardBanner from '../components/RewardBanner';
import RevealCountdown from '../components/RevealCountdown';
import BingoLetters from '../components/BingoLetters';
import { formatDate } from '../utils/dateFormatter';
import { getDaysRemaining } from '../utils/gameUtils';

export default function DashboardPage() {
    const { user } = useAuth();
    const { tenant } = useTenant();
    const api = useApi();

    const [games, setGames] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [tip, setTip] = useState<any>(null);
    const [tipLoading, setTipLoading] = useState(true);

    const isAdmin = user?.role === UserRole.COMPANY_ADMIN || user?.role === UserRole.DEPT_ADMIN;

    useEffect(() => {
        loadDashboardData();
    }, []);

    async function loadDashboardData() {
        try {
            setLoading(true);
            setTipLoading(true);

            // Load games for all users
            const gamesResult = await api.getGames();
            setGames(gamesResult.games || []);

            // Load random tip
            try {
                const tipResult = await api.getRandomTip();
                setTip(tipResult.tip);
            } catch (error) {
                console.error('Failed to load tip:', error);
            } finally {
                setTipLoading(false);
            }
        } catch (error) {
            console.error('Failed to load dashboard data:', error);
            setTipLoading(false);
        } finally {
            setLoading(false);
        }
    }

    const activeGames = games.filter((g) => g.status === 'active');

    return (
        <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                <div className="bg-surface rounded-lg shadow p-6 mb-6 border border-border">
                    <h2 className="text-2xl font-bold mb-2 text-text-primary">Welcome back, {user?.name}!</h2>
                </div>

                {/* Tip of the Day */}
                <div className="mb-6">
                    {tipLoading ? (
                        <TipCardSkeleton />
                    ) : tip ? (
                        <TipCard tip={tip} />
                    ) : null}
                </div>

                {/* Active Games */}
                {activeGames.length > 0 && (
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold mb-4">Your Active Games</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {activeGames.slice(0, 3).map((game) => (
                                <Link
                                    key={game.id}
                                    to={`/games/${game.id}`}
                                    className="block bg-surface rounded-lg shadow hover:shadow-lg transition p-6 border border-border"
                                >
                                    <h4 className="font-bold text-lg mb-2">{game.name}</h4>
                                    <div className="mb-3">
                                        <BingoLetters gameId={game.id} size="small" />
                                    </div>
                                    <p className="text-sm text-text-secondary mb-2 line-clamp-2">
                                        {game.description}
                                    </p>
                                    {game.rewardBanner && (
                                        <div className="mb-2">
                                            <RewardBanner reward={game.rewardBanner} compact />
                                        </div>
                                    )}
                                    {(() => {
                                        const days = getDaysRemaining(game.endDate);
                                        if (days === null) return null;
                                        if (days === 0) return <p className="text-xs font-semibold text-amber-600">⏰ Ends today!</p>;
                                        if (days < 0) return <p className="text-xs text-text-muted">Ended: {formatDate(game.endDate)}</p>;
                                        return <p className="text-xs font-semibold text-primary">⏳ {days} day{days !== 1 ? 's' : ''} left</p>;
                                    })()} 
                                </Link>
                            ))}
                        </div>
                        {activeGames.length > 3 && (
                            <Link
                                to="/games"
                                className="inline-block mt-4 text-blue-600 hover:text-blue-800"
                            >
                                View all games →
                            </Link>
                        )}
                    </div>
                )}

                {/* Reveal Countdown for Active Games - shown below games */}
                {activeGames.length > 0 && activeGames.map((game) => (
                    game.revealSchedule && (
                        <RevealCountdown
                            key={`reveal-${game.id}`}
                            game={game}
                            timezone={(tenant as any)?.timezone || 'America/New_York'}
                        />
                    )
                ))}

                {loading && (
                    <div className="text-center py-12 text-gray-500">
                        Loading...
                    </div>
                )}

                {!loading && activeGames.length === 0 && (
                    <div className="bg-surface rounded-lg shadow p-12 text-center mt-6 border border-border">
                        <p className="text-gray-500 text-lg">No active games yet</p>
                        <p className="text-gray-400 text-sm mt-2">
                            {isAdmin ? 'Create a game to get started' : 'Check back later'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
